import { PatientStartDateDto, PatientStartDateResponseDto } from "../../../../api-client";
import { useEffect, useState, useMemo } from 'react';
import { calculateCumulativePatients } from "../../helpers/patientStatsHelpers";
import useStore from "../../../../helpers/useStore";
import { BIContext } from "../../../../stores/BIDashboard/biDashboard.provider";
import { useDebounce } from "../../../Transactions/Insurance/Base/hooks/useDebounce";

interface PatientCumulativeData {
  locationId: string, 
  locationName: string, 
  date: Date, 
  cumulativeSum: number
}

const useCumulativePatientsStats = (binType: 'day' | 'week' | 'month' | 'year', startDate: Date, endDate: Date, locationIds?: string[]) => {
  const BIStore = useStore(BIContext); 
  const { getPatientStats } = BIStore; 

  // Debounce date changes
  const debounceStartDate = useDebounce(startDate, 300);
  const debounceEndDate = useDebounce(endDate, 300);

  // Memoize locationIds to avoid unnecessary re-renders
  const memoizedLocationIds = useMemo(() => {
    if (!locationIds || locationIds.length === 0) {
      return undefined;
    }
    return locationIds;
  }, [locationIds?.join(','), locationIds?.length]);

  // Store all fetched stats (for "all locations")
  const [allPatientStats, setAllPatientStats] = useState<PatientStartDateDto[]>([]);
  // Store filtered stats per selected locations
  const [patientStats, setPatientStats] = useState<PatientStartDateDto[]>([]);

  const [patientCumulativeResults, setPatientCumulativeResults] = useState<PatientCumulativeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fallbackUsed, setFallbackUsed] = useState<boolean>(false);
  const [usedStartDate, setUsedStartDate] = useState<Date | undefined>(undefined);
  const [usedEndDate, setUsedEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        let response: PatientStartDateResponseDto | undefined;
        if (debounceStartDate && debounceEndDate) {
          // console.log(`[Cumulative Patients] Fetching data for date range: ${debounceStartDate} - ${debounceEndDate}`);
          response = await getPatientStats(debounceStartDate, debounceEndDate);
          // console.log("[Cumulative Patients] Backend responded with:", response);
        } else {
          // console.log("[Cumulative Patients] Fetching all data");
        }

        if (response && response.patientStats.length > 0) {
          // console.log("[Cumulative Patients] is fallback", response.fallbackUsed);
          // console.log("[Cumulative Patients] used start date", response.usedStartDate);
          setAllPatientStats(response.patientStats);
          
          // Apply location filter immediately when setting data
          if (memoizedLocationIds && memoizedLocationIds.length > 0) {
            setPatientStats(response.patientStats.filter(p => memoizedLocationIds.includes(p.locationId)));
          } else {
            setPatientStats(response.patientStats);
          }
          
          setFallbackUsed(response.fallbackUsed);
          setUsedStartDate(response.usedStartDate ? new Date(response.usedStartDate) : undefined);
          setUsedEndDate(response.usedEndDate ? new Date(response.usedEndDate) : undefined);
        } else {
          // console.log("[Cumulative Patients] No data found for the specified range, even with fallback.");
          setAllPatientStats([]);
          setPatientStats([]);
        }
      } catch (error) {
        // console.log("[Cumulative Patients] Error fetching patient stats: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();   
  }, [debounceStartDate, debounceEndDate, getPatientStats]);

  // Filter locally whenever locations change (but not when data is initially loaded)
  useEffect(() => {
    if (!isLoading && allPatientStats.length > 0) {
      if (memoizedLocationIds && memoizedLocationIds.length > 0) {
        setPatientStats(allPatientStats.filter(p => memoizedLocationIds.includes(p.locationId)));
      } else {
        // No location selected → show all
        setPatientStats(allPatientStats);
      }
    }
  }, [memoizedLocationIds, allPatientStats, isLoading]);

  useEffect(() => {
    if (patientStats.length > 0) {
      const patientCumulatives = calculateCumulativePatients(patientStats, binType);
      setPatientCumulativeResults(patientCumulatives);
    } else {
      setPatientCumulativeResults([]);
    }
  }, [binType, patientStats]);

  const availableLocations = useMemo(() => {
    const locMap = new Map<string, string>();
    allPatientStats.forEach((p) => {
      if (p.locationId) locMap.set(p.locationId, p.locationName || p.locationId);
    });
    return Array.from(locMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allPatientStats]);

  return { patientCumulativeResults, isLoading, fallbackUsed, usedStartDate, usedEndDate, availableLocations };
};

export default useCumulativePatientsStats;
